//================================================================
//  Tab: RiskAndCompliance
//================================================================

//  Purpose: This tab displays all 'RiskAndCompliance' of an Application

//  Example:
//    <RiskAndCompliance></RiskAndCompliance>    

//================================================================

//Libraries
import React, { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

//Contexts
import { GetUser } from '../../../../../Library/GlobalContexts';

//Components
import PageComponent from '../../../../../Components/PageComponent/PageComponent';
import LiveField from '../../../../../Components/LiveField/LiveField';

//Functions
import DocumentListener from '../../../../../Library/DocumentListener';

//Images


export default function RiskAndCompliance() {

  //------------------------------------------------------
  //  useContext & React Router
  //------------------------------------------------------

  const getUser = useContext(GetUser);
  const params = useParams();

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Used to save page status > 'pending', 'onload', 'error-invalid', 'error-fatal'
  const [pageStatus, setPageStatus] = useState('pending');

  // Holds the application document from 
  const [applicationDoc, setApplicationDoc] = useState();

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------

  // Query Listener
  // - Get all Overview with the status of 'Active'
  useEffect(() => {

    const applicationId = params.id;

    if (applicationId === '') return;
    if (applicationId === null) return;

    function onLoadChange(document) {

      if (document === undefined) return setPageStatus('forbidden');

      setApplicationDoc(document);
      setPageStatus('onload')

    }

    function onError(error) {

      console.log(error);
      setPageStatus('error-fatal');

    }

    const unsubscribe = DocumentListener('applications', applicationId, onLoadChange, onLoadChange, onError);

    return () => {
      unsubscribe();
    };

    // eslint-disable-next-line
  }, [getUser]);

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  return (
    <PageComponent
      status={pageStatus}
      body={
        <div className='flex flex-col w-[50%] px-[20px]'>

          {/* MVA */}
          <LiveField
            type='choice'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'mva'}
            label={<label className='font-medium'>MVA</label>}
            defaultValue={applicationDoc?.mva}
            options={[
              'Yes',
              'No',
            ]}
            disabled={false} // indicator denoting whether the asset is classified as a Most Valuable Asset (score ≥ 61)
          ></LiveField>

          {/* BCP */}
          <LiveField
            type='choice'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'bcp'}
            label={<label className='font-medium'>BCP Defined</label>}
            defaultValue={applicationDoc?.bcp}
            options={[
              'Yes',
              'No',
              'N/A'
            ]}
          ></LiveField>

          {/* RPO - Data Recovery Point */}
          <LiveField
            type='choice'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'rpo'}
            label={<label className='font-medium'>Data Recovery Point</label>}
            defaultValue={applicationDoc?.rpo}
            options={[
              'Tier 0: 1 hour',
              'Tier 1: 4 hours',
              'Tier 2: 12 hours',
              'Tier 3: 24 hours',
              'Tier 4: 48 hours',
              'Tier 5: 4 days',
              'Tier 6: Best Effort',
            ]}
          ></LiveField>

          {/* RTO - Recovery Time Objective */}
          <LiveField
            type='choice'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'rto'}
            label={<label className='font-medium'>RTO</label>}
            defaultValue={applicationDoc?.rto}
            options={[
              '0 - 4 hours',
              '4 - 12 hours',
              '12 - 24 hours',
              '24 - 48 hours',
              '7 - 14 days',
              'Best Effort ',
            ]}
          ></LiveField>

          {/* MTO/MAO in Hours */}
          <LiveField
            type='choice'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'mtohours'}
            label={<label className='font-medium'>MTO/MAO in Hours</label>}
            defaultValue={applicationDoc?.mtohours}
            options={[
              'Tier 0',
              'Tier 1',
              'Tier 2',
              'Tier 3',
              'Tier 4',
              'Tier 5',
              'Tier 6',
            ]}
          ></LiveField>

          {/* Link to DR */}
          <LiveField
            type='text'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'linktodr'}
            label={<label className='font-medium'>Link to DR</label>}
            placeholder={'Enter link or N/A'}
            defaultValue={applicationDoc?.linktodr}
            validation={{
              type: 'link',
            }}
          ></LiveField>

          {/* DR Frequency */}
          <LiveField
            type='choice'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'drfrequency'}
            label={<label className='font-medium'>DR Frequency</label>}
            defaultValue={applicationDoc?.drfrequency}
            options={[
              'Annually',
              'Semi-Annually',
              'N/A'
            ]}
          ></LiveField>

          {/* Next Compliance Review Date */}
          <LiveField
            type='date-string'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'nextcompreviewdate'}
            label={<label className='font-medium'>Next Compliance Review Date</label>}
            defaultValue={applicationDoc?.nextcompreviewdate}
          ></LiveField>

          {/* Applicable Controls */}
          <LiveField
            type='multiline'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'applicablecontrols'}
            label={<label className='font-medium'>Applicable Controls</label>}
            placeholder={'All the controls applicable for the application'}
            defaultValue={applicationDoc?.applicablecontrols}
            validation={{
              type: 'text',
              required: true,
            }}
          ></LiveField>

          {/* Data Classification */}
          <LiveField
            type='choice'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'dataclassification'}
            label={<label className='font-medium'>Data Classification</label>}
            defaultValue={applicationDoc?.dataclassification}
            options={[
              'Public',
              'Internal',
              'Confidential',
              'Restricted',
            ]}
          ></LiveField>

          {/* Confidentiality Rating */}
          <LiveField
            type='choice'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'confidentialityrating'}
            label={<label className='font-medium'>Confidentiality Rating</label>}
            defaultValue={applicationDoc?.confidentialityrating}
            options={[
              '1',
              '2',
              '3',
              '4',
              '5',
            ]}
          ></LiveField>

          {/* Integrity Rating */}
          <LiveField
            type='choice'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'integrityrating'}
            label={<label className='font-medium'>Integrity Rating</label>}
            defaultValue={applicationDoc?.integrityrating}
            options={[
              '1',
              '2',
              '3',
              '4',
              '5',
            ]}
          ></LiveField>

          {/* Availability Rating */}
          <LiveField
            type='choice'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'availabilityrating'}
            label={<label className='font-medium'>Availability Rating</label>}
            defaultValue={applicationDoc?.availabilityrating}
            options={[
              '1',
              '2',
              '3',
              '4',
              '5',
            ]}
          ></LiveField>

          {/* Sensitivity Rating */}
          <LiveField
            type='text'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'sensitivityrating'}
            label={<label className='font-medium'>Sensitivity Rating</label>}
            defaultValue={applicationDoc?.sensitivityrating}
            disabled={true} // Calculated by backend service. Value calculated automatically based on greatest value between "confidentialityrating" and "integrityrating"
          ></LiveField>

          {/* Asset Score */}
          <LiveField
            type='text'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'assetscore'}
            label={<label className='font-medium'>Asset Score</label>}
            defaultValue={applicationDoc?.assetscore}
            disabled={true} // Calculated by backend service. (confidentialityrating + integrityrating + availabilityrating) × sensitivityrating. Used to determine MVA status
          ></LiveField>

          {/* Asset Tier */}
          <LiveField
            type='choice'
            collectionid='applications'
            documentid={applicationDoc?.applicationid}
            fieldpath={'assetier'}
            label={<label className='font-medium'>Asset Tier</label>}
            defaultValue={applicationDoc?.assetier}
            options={[
              '1',
              '2',
              '3',
              '4',
              '5',
            ]}
          ></LiveField>

        </div>
      }
    ></PageComponent>
  )
}
